import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  X, Check, Copy, Clock, ChevronRight, ArrowLeft, CalendarDays, MessageCircle,
} from "lucide-react";
import { api, formatApiError, brl, PIX, CONTACTS } from "@/lib/api";
import { nextDays, prettyDate } from "@/lib/dates";

const MAX_SLOTS = 3;
const emptyForm = { name: "", instagram: "", phone: "" };

function Countdown({ expiresAt }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000));
      setLeft(diff);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  const m = String(Math.floor(left / 60)).padStart(2, "0");
  const s = String(left % 60).padStart(2, "0");
  const danger = left <= 120;
  return (
    <span
      data-testid="pix-countdown"
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-bold tabular-nums ${
        danger ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
      }`}
    >
      <Clock className="h-4 w-4" /> {m}:{s}
    </span>
  );
}

export default function BookingDialog({ space, open, onOpenChange }) {
  const days = useMemo(() => nextDays(30), []);
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(days[0].iso);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selected, setSelected] = useState([]); // array of slot objects
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [reservation, setReservation] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(1);
      setSelectedDate(days[0].iso);
      setSelected([]);
      setForm(emptyForm);
      setReservation(null);
    }
  }, [open, space, days]);

  useEffect(() => {
    if (!open || !space) return;
    setLoadingSlots(true);
    setSelected([]);
    api
      .get("/availability", { params: { space_id: space.id, date: selectedDate } })
      .then((res) => setSlots((res.data.slots || []).filter((s) => !s.is_past)))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoadingSlots(false));
  }, [open, space, selectedDate]);

  if (!space) return null;
  const isChurras = space.type === "churrasqueira";
  const total = selected.reduce((sum, x) => sum + x.price, 0);

  const toggleSlot = (s) => {
    const exists = selected.find((x) => x.hour === s.hour);
    if (exists) {
      setSelected(selected.filter((x) => x.hour !== s.hour));
      return;
    }
    if (selected.length >= MAX_SLOTS) {
      toast.error(`Você pode escolher até ${MAX_SLOTS} ${isChurras ? "períodos" : "horários"} por pedido.`);
      return;
    }
    setSelected([...selected, s].sort((a, b) => a.hour - b.hour));
  };

  const slotLabel = (s) =>
    isChurras ? `${s.label} · ${s.range_label}` : `${s.label} - ${s.end_label}`;

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Informe seu nome.");
      return;
    }
    if (!form.phone.trim()) {
      toast.error("Informe seu telefone/WhatsApp.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/reservations", {
        space_id: space.id,
        date: selectedDate,
        hours: selected.map((s) => s.hour),
        name: form.name.trim(),
        instagram: form.instagram.trim(),
        phone: form.phone.trim(),
      });
      setReservation(res.data);
      setStep(3);
      toast.success("Reserva solicitada! Faça o PIX para confirmar.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copyPix = () => {
    navigator.clipboard?.writeText(PIX.key);
    toast.success("Chave PIX copiada!");
  };

  const waMessage = reservation
    ? encodeURIComponent(
        `Olá! Segue o comprovante do PIX da minha reserva na Dubal Esportes:\n` +
          `Espaço: ${reservation.space_name}\n` +
          `Data: ${prettyDate(reservation.date)}\n` +
          `${isChurras ? "Períodos" : "Horários"}: ${reservation.time_label}\n` +
          `Total: ${brl(reservation.price)}`
      )
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-dialog"
        className="max-w-lg gap-0 overflow-hidden rounded-3xl border-0 bg-white p-0 shadow-2xl"
      >
        <DialogTitle className="sr-only">Reservar {space.name}</DialogTitle>
        <DialogDescription className="sr-only">
          Escolha data, horários e informe seus contatos para reservar {space.name}.
        </DialogDescription>

        {/* Header */}
        <div className="relative bg-[#122018] px-6 py-5">
          <button
            onClick={() => onOpenChange(false)}
            data-testid="booking-close"
            className="absolute right-4 top-4 rounded-full p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-300">
            Reserva · {space.category_label}
          </p>
          <h3 className="font-display text-2xl font-bold text-white">{space.name}</h3>
          <div className="mt-3 flex gap-1.5">
            {[1, 2, 3].map((n) => (
              <span
                key={n}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  step >= n ? "bg-[#E8611A]" : "bg-white/20"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
          {/* STEP 1 - date + slots */}
          {step === 1 && (
            <div className="animate-fade-up">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                <CalendarDays className="h-4 w-4 text-[#1B7A45]" /> Escolha o dia
              </div>
              <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-2">
                {days.map((d) => {
                  const active = d.iso === selectedDate;
                  return (
                    <button
                      key={d.iso}
                      data-testid={`date-${d.iso}`}
                      onClick={() => setSelectedDate(d.iso)}
                      className={`flex min-w-[64px] flex-col items-center rounded-2xl border px-3 py-2 transition-all ${
                        active
                          ? "border-[#1B7A45] bg-[#1B7A45] text-white shadow-sm"
                          : "border-slate-200 bg-white text-slate-700 hover:border-[#1B7A45]"
                      }`}
                    >
                      <span className="text-[11px] font-medium uppercase opacity-80">
                        {d.isToday ? "Hoje" : d.isTomorrow ? "Amanhã" : d.weekday}
                      </span>
                      <span className="font-display text-xl font-bold">{d.day}</span>
                      <span className="text-[11px] opacity-80">{d.month}</span>
                    </button>
                  );
                })}
              </div>

              <div className="mb-2 mt-4 flex items-center justify-between text-sm font-semibold text-slate-700">
                <span>{isChurras ? "Períodos disponíveis" : "Horários disponíveis"}</span>
                <span className="text-xs font-normal text-slate-400">
                  Até {MAX_SLOTS} por pedido
                </span>
              </div>

              {loadingSlots ? (
                <div className="grid grid-cols-3 gap-2">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {slots.map((s) => {
                    const active = selected.find((x) => x.hour === s.hour);
                    return (
                      <button
                        key={s.hour}
                        data-testid={`slot-${s.label}`}
                        disabled={!s.available}
                        onClick={() => toggleSlot(s)}
                        className={`relative flex flex-col items-center rounded-xl border px-2 py-2 text-center transition-all ${
                          !s.available
                            ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300 line-through"
                            : active
                            ? "border-[#1B7A45] bg-[#EAF5EE] text-[#0E4325] ring-2 ring-[#1B7A45]"
                            : "border-slate-200 bg-white text-slate-800 hover:border-[#1B7A45]"
                        }`}
                      >
                        {active && (
                          <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#1B7A45] text-white">
                            <Check className="h-3 w-3" />
                          </span>
                        )}
                        <span className="text-sm font-bold">{s.label}</span>
                        <span className="text-[11px] font-medium text-[#E8611A]">
                          {s.available ? brl(s.price) : "Ocupado"}
                        </span>
                        {s.available && (
                          <span className="text-[10px] uppercase text-slate-400">
                            {s.caption || s.period}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {selected.length > 0 && (
                <div className="mt-4 flex items-center justify-between rounded-xl bg-[#EAF5EE] px-4 py-2.5">
                  <span className="text-sm font-medium text-[#0E4325]">
                    {selected.length} {isChurras ? "período(s)" : "horário(s)"} selecionado(s)
                  </span>
                  <span className="font-display text-lg font-black text-[#1B7A45]">{brl(total)}</span>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 - checkout */}
          {step === 2 && (
            <div className="animate-fade-up">
              <div className="mb-4 rounded-2xl bg-slate-50 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Espaço</span>
                  <span className="font-semibold text-slate-800">{space.name}</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between text-sm">
                  <span className="text-slate-500">Data</span>
                  <span className="font-semibold text-slate-800">{prettyDate(selectedDate)}</span>
                </div>
                <div className="mt-2 border-t border-slate-200 pt-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                    {isChurras ? "Períodos" : "Horários"}
                  </span>
                  <div className="mt-1 space-y-1">
                    {selected.map((s) => (
                      <div key={s.hour} className="flex items-center justify-between text-sm">
                        <span className="text-slate-700">{slotLabel(s)}</span>
                        <span className="font-medium text-slate-800">{brl(s.price)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
                  <span className="text-sm font-semibold text-slate-600">Total</span>
                  <span className="font-display text-xl font-black text-[#E8611A]">{brl(total)}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="name" className="text-sm font-semibold text-slate-700">
                    Nome *
                  </Label>
                  <Input
                    id="name"
                    data-testid="input-name"
                    placeholder="Seu nome completo"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 rounded-xl border-slate-200"
                  />
                </div>
                <div>
                  <Label htmlFor="ig" className="text-sm font-semibold text-slate-700">
                    @ do Instagram
                  </Label>
                  <Input
                    id="ig"
                    data-testid="input-instagram"
                    placeholder="@seuinstagram"
                    value={form.instagram}
                    onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                    className="mt-1.5 rounded-xl border-slate-200"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-sm font-semibold text-slate-700">
                    Telefone / WhatsApp *
                  </Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    placeholder="(48) 9 9999-9999"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="mt-1.5 rounded-xl border-slate-200"
                  />
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Após confirmar, mostraremos a chave <strong>PIX</strong>. {PIX.warning}
              </div>
            </div>
          )}

          {/* STEP 3 - PIX */}
          {step === 3 && reservation && (
            <div className="animate-fade-up text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#EAF5EE]">
                <Check className="h-7 w-7 text-[#1B7A45]" />
              </div>
              <h4 className="mt-3 font-display text-xl font-bold text-slate-900">
                Reserva solicitada!
              </h4>
              <p className="mt-1 text-sm text-slate-500">
                Pague via PIX para confirmar. Sua reserva expira em:
              </p>
              <div className="mt-3">
                <Countdown expiresAt={reservation.expires_at} />
              </div>

              <div className="mt-4 rounded-xl bg-slate-50 p-3 text-left text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">{isChurras ? "Períodos" : "Horários"}</span>
                  <span className="font-semibold text-slate-800">{reservation.time_label}</span>
                </div>
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Chave PIX (Celular)
                </p>
                <div className="mt-1 flex items-center justify-between gap-3">
                  <span className="font-display text-xl font-bold text-slate-900">{PIX.key}</span>
                  <Button
                    data-testid="pix-copy-key-btn"
                    onClick={copyPix}
                    variant="outline"
                    className="rounded-xl border-[#1B7A45] text-[#1B7A45] hover:bg-[#EAF5EE]"
                  >
                    <Copy className="mr-1.5 h-4 w-4" /> Copiar
                  </Button>
                </div>
                <div className="mt-3 border-t border-slate-200 pt-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Titular</span>
                    <span className="font-semibold text-slate-800">{PIX.holder}</span>
                  </div>
                  <div className="mt-1 flex justify-between">
                    <span className="text-slate-500">Valor</span>
                    <span className="font-bold text-[#E8611A]">{brl(reservation.price)}</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
                ⚠️ {PIX.warning}
              </div>

              {/* WhatsApp comprovante */}
              <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-sm font-semibold text-[#0E4325]">
                  Envie o comprovante em um destes WhatsApp:
                </p>
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <a
                    href={`${CONTACTS.felipe.url}?text=${waMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="send-proof-felipe"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1B7A45] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#156337]"
                  >
                    <MessageCircle className="h-4 w-4" /> {CONTACTS.felipe.number}
                  </a>
                  <a
                    href={`${CONTACTS.villalobos.url}?text=${waMessage}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="send-proof-villalobos"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#1B7A45] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#156337]"
                  >
                    <MessageCircle className="h-4 w-4" /> {CONTACTS.villalobos.number}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-3 border-t border-slate-100 px-6 py-4">
          {step === 1 && (
            <>
              {selected.length > 0 && (
                <span className="text-sm font-semibold text-slate-700">
                  Total <span className="text-[#E8611A]">{brl(total)}</span>
                </span>
              )}
              <Button
                data-testid="booking-next-1"
                disabled={selected.length === 0}
                onClick={() => setStep(2)}
                className="ml-auto rounded-full bg-[#1B7A45] px-7 py-5 text-base font-semibold text-white hover:bg-[#156337] disabled:opacity-40"
              >
                Continuar <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="rounded-full text-slate-500"
                data-testid="booking-back-2"
              >
                <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
              </Button>
              <Button
                data-testid="btn-confirm-booking"
                disabled={submitting}
                onClick={submit}
                className="ml-auto rounded-full bg-[#E8611A] px-7 py-5 text-base font-semibold text-white hover:bg-[#CF5212] disabled:opacity-50"
              >
                {submitting ? "Enviando..." : "Confirmar e ver PIX"}
              </Button>
            </>
          )}
          {step === 3 && (
            <Button
              data-testid="booking-done"
              onClick={() => onOpenChange(false)}
              className="mx-auto rounded-full bg-[#1B7A45] px-8 py-5 text-base font-semibold text-white hover:bg-[#156337]"
            >
              Concluir
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
